import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

// Enforce safe link attributes on any <a> that survives sanitization
if (typeof window !== 'undefined' && DOMPurify.addHook) {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node && node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });
}

/**
 * SafeHtml renders sanitized HTML produced by our RichTextEditor.
 * - Whitelists only formatting/structural tags.
 * - Forces all links to open in a new tab with safe rel attributes.
 * - Backwards compatible with plain-text bodies (renders newlines as <br/>).
 */
export default function SafeHtml({ html, className = '', ...rest }) {
  const clean = useMemo(() => {
    if (!html) return '';
    const source = String(html);
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(source);
    const asHtml = looksLikeHtml
      ? source
      : source.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');

    return DOMPurify.sanitize(asHtml, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'code', 'pre',
        'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'a', 'span', 'hr',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      ALLOW_DATA_ATTR: false,
    });
  }, [html]);

  return (
    <div
      className={`prose prose-sm max-w-none [&_a]:text-[#002FA7] [&_a]:underline [&_h1]:font-display [&_h2]:font-display [&_h3]:font-display [&_p]:my-2 ${className}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: clean }}
      {...rest}
    />
  );
}
